import { ConfigProvider, Form, TableProps } from 'antd'
import vi_VN from 'antd/es/locale/vi_VN'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { apiGetOutletSaleEfficiency } from '../../api'
import FormSearch from '../../components/form'
import CompanySelect from '../../components/outlet/CompanySelect'
import OutletSelect from '../../components/outlet/OutletSelect'
import DateRangePresets from '../../components/outlet/DateRangePresets'
import UserSelect from '../../components/outlet/UserSelect'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { ISaleRow } from '../../models/outlet.modal'
import { toastError } from '../../utils/toast'

const formatNumber = (v?: number | null) => (v == null ? '—' : v.toLocaleString('vi-VN'))
const formatCurrency = (v?: number | null) => (v == null ? '—' : `${v.toLocaleString('vi-VN')}₫`)
const formatPercent = (rate?: number | null) => (rate == null ? '—' : `${(rate * 100).toFixed(1)}%`)

type SearchState = {
    fromDate?: string
    toDate?: string
    cpCodes?: string[]
    outletIds?: string[]
    ownerUserIds?: number[]
}

export default function OutletSaleEfficiency() {
    const [data, setData] = useState<ISaleRow[]>([])
    const [search, setSearch] = useState<SearchState>({})
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp: any = await apiGetOutletSaleEfficiency({
                fromDate: search.fromDate,
                toDate: search.toDate,
                cpCodes: search.cpCodes,
                outletIds: search.outletIds,
                ownerUserIds: search.ownerUserIds,
            })
            setData(resp?.data?.data ?? [])
        } catch (error) {
            console.log(error)
            toastError('Không tải được báo cáo hiệu quả sale')
            setData([])
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleSearch = (values: any) => {
        const range = values.range as [dayjs.Dayjs, dayjs.Dayjs] | undefined
        setSearch({
            fromDate: range?.[0]?.format('YYYY-MM-DD'),
            toDate: range?.[1]?.format('YYYY-MM-DD'),
            cpCodes: values.cpCodes,
            outletIds: values.outletIds,
            ownerUserIds: values.ownerUserIds,
        })
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
    }

    const columns: TableProps<ISaleRow>['columns'] = [
        {
            key: 'ownerUserName',
            title: 'Sale',
            render: (_, r) => r.ownerUserName || `#${r.ownerUserId}`,
        },
        {
            key: 'outletCount',
            title: 'Số đại lý phụ trách',
            align: 'right',
            render: (_, r) => formatNumber(r.outletCount),
        },
        {
            key: 'activeOutlets',
            title: 'Đại lý active',
            align: 'right',
            render: (_, r) => formatNumber(r.activeOutlets),
        },
        {
            key: 'simAllocated',
            title: 'SIM giao',
            align: 'right',
            render: (_, r) => formatNumber(r.simAllocated),
        },
        {
            key: 'simActivated',
            title: 'SIM đấu mới',
            align: 'right',
            render: (_, r) => formatNumber(r.simActivated),
        },
        {
            key: 'renewalCount',
            title: 'Lượt gia hạn',
            align: 'right',
            render: (_, r) => formatNumber(r.renewalCount),
        },
        {
            key: 'activationRate',
            title: 'Tỷ lệ đấu',
            align: 'right',
            render: (_, r) => formatPercent(r.activationRate),
        },
        {
            key: 'conversionRate',
            title: 'Tỷ lệ chuyển đổi',
            align: 'right',
            render: (_, r) => formatPercent(r.conversionRate),
        },
        {
            key: 'revenue',
            title: 'Doanh thu',
            align: 'right',
            render: (_, r) => formatCurrency(r.revenue),
        },
    ]

    return (
        <ConfigProvider locale={vi_VN}>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='range' label='Khoảng ngày' style={{ minWidth: 300 }}>
                    <DateRangePresets />
                </Form.Item>
                <Form.Item name='cpCodes' label='Công ty' style={{ minWidth: 240 }}>
                    <CompanySelect multiple onlyWithOutlets placeholder='Chọn công ty' />
                </Form.Item>
                <Form.Item name='outletIds' label='Đại lý' style={{ minWidth: 240 }}>
                    <OutletSelect multiple placeholder='Chọn đại lý' />
                </Form.Item>
                <Form.Item name='ownerUserIds' label='Sale' style={{ minWidth: 240 }}>
                    <UserSelect multiple placeholder='Chọn sale' />
                </Form.Item>
            </FormSearch>

            <CustomTable
                namePage='sale'
                rowKey='ownerUserId'
                columns={columns}
                dataSource={data}
                total={data.length}
                pageSize={data.length || 10}
                setPageSize={() => { }}
                currentPage={1}
                setCurrentPage={() => { }}
                isHiddenPagination
            />
        </ConfigProvider>
    )
}
